/**
 * Receiving-bank-account helpers shared by the POS Payment dialog and the Khata Record Payment
 * dialog (T-479). Mirrors the backend `isBankSettledMethod` in
 * `backend/src/modules/accounting/payment-method-accounts.ts`.
 */

export interface BankAccountOption {
  id: string;
  name: string;
  bankName: string;
}

const BANK_SETTLED_METHODS: readonly string[] = ['CARD', 'MOBILE_WALLET', 'QR_CODE', 'BANK_TRANSFER'];

/** Tender methods whose money lands in the bank (1020) and therefore needs a receiving account. */
export function isBankSettledMethod(method: string): boolean {
  return BANK_SETTLED_METHODS.includes(method);
}

const LAST_BANK_KEY = 'clearring.lastBankAccountId';

function readLastBankAccountId(): string | null {
  try {
    return globalThis.localStorage?.getItem(LAST_BANK_KEY) ?? null;
  } catch {
    return null;
  }
}

/** Remember the account used for the last bank-settled tender on this device. */
export function rememberBankAccountId(id: string): void {
  if (!id) return;
  try {
    globalThis.localStorage?.setItem(LAST_BANK_KEY, id);
  } catch {
    /* storage unavailable — preselection is a convenience only */
  }
}

/**
 * Account to preselect: the last-used one on this device if it is still active, else the first.
 * Returns '' when the tenant has no accounts.
 */
export function getDefaultBankAccountId(accounts: readonly BankAccountOption[]): string {
  if (accounts.length === 0) return '';
  const last = readLastBankAccountId();
  if (last && accounts.some((a) => a.id === last)) return last;
  return accounts[0].id;
}

/**
 * The picker is only shown when there is a real choice. With exactly one account the server
 * defaults to it (cashier adds no step); with none, nothing can be recorded against a bank.
 */
export function shouldShowBankPicker(accounts: readonly BankAccountOption[]): boolean {
  return accounts.length > 1;
}

/**
 * The bankAccountId to submit for a tender: the explicit choice, else (when a picker was shown)
 * the preselected default. `undefined` lets the server apply its single-account default.
 */
export function effectiveBankAccountId(
  method: string,
  chosenId: string,
  accounts: readonly BankAccountOption[],
): string | undefined {
  if (!isBankSettledMethod(method)) return undefined;
  if (chosenId) return chosenId;
  if (shouldShowBankPicker(accounts)) return getDefaultBankAccountId(accounts) || undefined;
  return undefined;
}
